import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, CheckCircle2, Star, Printer } from 'lucide-react';

const STATUS_COLORS = {
  DRAFT: 'secondary', SUBMITTED: 'default', UNDER_REVIEW: 'default',
  APPROVED: 'default', REJECTED: 'destructive',
};

function renderField(field, value, onChange, readOnly) {
  const key = field.key;
  const common = { disabled: readOnly };

  switch (field.type) {
    case 'section_header':
      return (
        <div className="pt-4 pb-1">
          <h3 className="text-base font-semibold text-foreground">{field.label}</h3>
          <Separator className="mt-2" />
        </div>
      );
    case 'divider':
      return <Separator className="my-2" />;
    case 'short_text':
    case 'email':
    case 'phone':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <Input type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={field.placeholder || ''} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'long_text':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <Textarea rows={4} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={field.placeholder || ''} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'number':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <Input type="number" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'date':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <Input type="date" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'time':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <Input type="time" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'dropdown':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <Select value={value || ''} onValueChange={v => onChange(key, v)} disabled={readOnly}>
            <SelectTrigger data-testid={`fill-${key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case 'multi_select':
    case 'checkbox': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.help_text && <p className="text-xs text-muted-foreground mb-1">{field.help_text}</p>}
          <div className="flex flex-wrap gap-2 mt-1">
            {(field.options || []).map(o => {
              const checked = selected.includes(o);
              return (
                <Button key={o} type="button" variant={checked ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
                  disabled={readOnly}
                  onClick={() => {
                    if (checked) onChange(key, selected.filter(x => x !== o));
                    else onChange(key, [...selected, o]);
                  }}
                  data-testid={`fill-${key}-${o}`}
                >{o}</Button>
              );
            })}
          </div>
        </div>
      );
    }
    case 'radio':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {(field.options || []).map(o => (
              <Button key={o} type="button" variant={value === o ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
                disabled={readOnly} onClick={() => onChange(key, o)}
              >{o}</Button>
            ))}
          </div>
        </div>
      );
    case 'yes_no':
      return (
        <div className="flex items-center justify-between py-1">
          <div>
            <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
            {field.help_text && <p className="text-xs text-muted-foreground">{field.help_text}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant={value === 'Yes' ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-3"
              disabled={readOnly} onClick={() => onChange(key, 'Yes')} data-testid={`fill-${key}-yes`}
            >Yes</Button>
            <Button type="button" variant={value === 'No' ? 'destructive' : 'outline'} size="sm" className="h-7 text-xs px-3"
              disabled={readOnly} onClick={() => onChange(key, 'No')} data-testid={`fill-${key}-no`}
            >No</Button>
          </div>
        </div>
      );
    case 'rating': {
      const stars = [1, 2, 3, 4, 5];
      const current = parseInt(value) || 0;
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <div className="flex gap-1 mt-1">
            {stars.map(s => (
              <button key={s} type="button" disabled={readOnly}
                className={`p-1 ${s <= current ? 'text-yellow-500' : 'text-muted-foreground/30'}`}
                onClick={() => onChange(key, s.toString())}
              ><Star className="w-6 h-6" fill={s <= current ? 'currentColor' : 'none'} /></button>
            ))}
          </div>
        </div>
      );
    }
    case 'address':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <Textarea rows={2} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder="Full address..." {...common} data-testid={`fill-${key}`} />
        </div>
      );
    case 'file_upload':
    case 'image_upload':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <Input type="file" {...common} className="text-sm" data-testid={`fill-${key}`} />
        </div>
      );
    case 'signature':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <div className="border rounded-md p-4 bg-muted/20 text-center text-sm text-muted-foreground">
            {value ? <p className="font-medium text-foreground">Signed: {value}</p> : (
              <>
                <Input placeholder="Type your full name as signature" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={`fill-${key}`} />
              </>
            )}
          </div>
        </div>
      );
    case 'participant_lookup':
    case 'staff_lookup':
    case 'shift_lookup':
      return (
        <div>
          <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <Input value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={`Enter ${field.type.replace('_lookup', '')} ID or name`} {...common} data-testid={`fill-${key}`} />
        </div>
      );
    default:
      return (
        <div>
          <Label className="text-sm">{field.label}</Label>
          <Input value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={`fill-${key}`} />
        </div>
      );
  }
}

export default function FormFillPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [schema, setSchema] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [submission, setSubmission] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, sRes] = await Promise.all([
          api.get(`/forms/assignments/${assignmentId}`),
          api.get(`/forms/assignments/${assignmentId}/submission`),
        ]);
        const a = aRes.data.data;
        setAssignment(a);
        setTemplateName(a.template?.name || 'Form');
        setSchema(a.version?.schema_json || []);
        if (sRes.data.data) {
          setSubmission(sRes.data.data);
          setFormData(sRes.data.data.data_json || {});
        }
      } catch { toast.error('Failed to load form'); }
      setLoading(false);
    };
    load();
  }, [assignmentId]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const isReadOnly = submission && ['APPROVED', 'LOCKED', 'SUBMITTED', 'UNDER_REVIEW'].includes(submission.status);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (submission) {
        await api.put(`/forms/submissions/${submission.id}/draft`, { data_json: formData });
        toast.success('Draft saved');
      } else {
        const { data } = await api.post('/forms/submissions', { assignment_id: assignmentId, data_json: formData });
        setSubmission(data.data);
        toast.success('Draft created');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const missing = schema.filter(f => f.required && f.key && !['section_header', 'divider'].includes(f.type)).filter(f => {
      const v = formData[f.key];
      return !v || (Array.isArray(v) && v.length === 0);
    });
    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.map(f => f.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      let subId = submission?.id;
      if (!subId) {
        const { data } = await api.post('/forms/submissions', { assignment_id: assignmentId, data_json: formData });
        subId = data.data.id;
        setSubmission(data.data);
      } else {
        await api.put(`/forms/submissions/${subId}/draft`, { data_json: formData });
      }
      const { data: result } = await api.post(`/forms/submissions/${subId}/submit`);
      setSubmission(prev => ({ ...prev, status: result.data.status }));
      setAssignment(prev => ({ ...prev, status: 'SUBMITTED' }));
      toast.success('Form submitted successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    setSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading form...</div>;
  if (!assignment) return <div className="text-center py-20 text-muted-foreground">Assignment not found</div>;

  const assignmentStatus = assignment.status;
  const submissionStatus = submission?.status;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="form-fill-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{templateName}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant={STATUS_COLORS[submissionStatus || assignmentStatus] || 'secondary'}>
                {submissionStatus || assignmentStatus}
              </Badge>
              {assignment.due_date && <span className="text-xs text-muted-foreground">Due: {assignment.due_date}</span>}
            </div>
          </div>
        </div>
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/forms/assignments/${assignmentId}/print`)} data-testid="print-form-btn">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn">
              <Send className="w-4 h-4 mr-2" />{submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        )}
        {isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/forms/assignments/${assignmentId}/print`)} data-testid="print-form-btn">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        )}
      </div>
      {isReadOnly && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-3 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800 font-medium">
              This form has been {submissionStatus?.toLowerCase()}. It is now read-only.
            </p>
          </CardContent>
        </Card>
      )}

      {submission?.rejection_reason && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3">
            <p className="text-sm text-red-800 font-medium">Rejected: {submission.rejection_reason}</p>
          </CardContent>
        </Card>
      )}

      {/* Form Fields */}
      <Card>
        <CardContent className="py-6 space-y-5">
          {schema.map((field, i) => (
            <div key={field.key || `field-${i}`}>
              {renderField(field, formData[field.key], handleChange, isReadOnly)}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Bottom Actions */}
      {!isReadOnly && (
        <div className="flex items-center justify-end gap-2 pb-8">
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />Save Draft
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            <Send className="w-4 h-4 mr-2" />Submit Form
          </Button>
        </div>
      )}
    </div>
  );
}
